import React, { useState } from 'react';
import { read, utils } from 'xlsx';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { FileSpreadsheet, Download, Upload } from 'lucide-react';

export function DataImport() {
  const [importing, setImporting] = useState(false);

  const downloadTemplate = () => {
    const workbook = utils.book_new();

    const statesData = [
      ['code', 'name', 'about_template', 'why_choose_us_template', 'benefits_template'],
      ['IL', 'Illinois', 'Serving {city} with professional plumbing services', 'Choose our {city} plumbers for reliable service', 'Get peace of mind with our satisfaction guarantee'],
      ['NY', 'New York', 'Expert plumbing services in {city}', 'Our {city} plumbers are licensed and insured', 'Benefit from our experience']
    ];
    const statesSheet = utils.aoa_to_sheet(statesData);
    utils.book_append_sheet(workbook, statesSheet, 'States');

    const citiesData = [
      ['state_code', 'name', 'slug', 'zip_codes', 'description', 'county', 'latitude', 'longitude'],
      ['IL', 'Chicago', 'chicago', '60601,60602,60603', 'Professional plumbing services in Chicago', 'Cook', '41.8781', '-87.6298'],
      ['NY', 'New York', 'new-york', '10001,10002,10003', 'Expert plumbing in New York City', 'New York', '40.7128', '-74.0060']
    ];
    const citiesSheet = utils.aoa_to_sheet(citiesData);
    utils.book_append_sheet(workbook, citiesSheet, 'Cities');

    const servicesData = [
      ['title', 'slug', 'description', 'image_url', 'features'],
      ['Emergency Plumbing', 'emergency-plumbing', '24/7 emergency services', 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39', 'Available 24/7,Fast response,Licensed pros'],
      ['Drain Cleaning', 'drain-cleaning', 'Professional drain cleaning', 'https://images.unsplash.com/photo-1584677626646-7c8f83690304', 'Hydro jetting,Camera inspection']
    ];
    const servicesSheet = utils.aoa_to_sheet(servicesData);
    utils.book_append_sheet(workbook, servicesSheet, 'Services');

    // Generate Excel file
    const wbout = utils.write(workbook, { bookType: 'xlsx', type: 'array' });
    
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nearplumbers-import-template.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      
      // Process each sheet
      for (const sheetName of workbook.SheetNames) {
        const rows = utils.sheet_to_json(workbook.Sheets[sheetName]);
        
        switch (sheetName.toLowerCase()) {
          case 'states':
            await importStates(rows);
            break;
          case 'cities':
            await importCities(rows);
            break;
          case 'services':
            await importServices(rows);
            break;
        }
      }
      
      toast.success('Data imported successfully');
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error importing data');
    } finally {
      setImporting(false);
    }
  };

  const importStates = async (rows: any[]) => {
    for (const row of rows) {
      const { data: stateData } = await supabase
        .from('states')
        .select('id')
        .eq('code', row.code)
        .single();

      const stateRecord = {
        code: row.code,
        name: row.name,
        about_template: row.about_template,
        why_choose_us_template: row.why_choose_us_template,
        benefits_template: row.benefits_template
      };

      if (stateData) {
        await supabase
          .from('states')
          .update(stateRecord)
          .eq('id', stateData.id);
      } else {
        await supabase
          .from('states')
          .insert(stateRecord);
      }
    }
  };

  const importCities = async (rows: any[]) => {
    for (const row of rows) {
      // Get state ID from code
      const { data: stateData } = await supabase
        .from('states')
        .select('id')
        .eq('code', row.state_code)
        .single();

      if (!stateData) {
        console.error(`State not found for code: ${row.state_code}`);
        continue;
      }

      const { data: cityData } = await supabase
        .from('cities')
        .select('id')
        .eq('state_id', stateData.id)
        .eq('name', row.name)
        .single();

      const cityRecord = {
        state_id: stateData.id,
        name: row.name,
        slug: row.slug,
        zip_codes: row.zip_codes.split(','),
        description: row.description,
        county: row.county,
        latitude: parseFloat(row.latitude),
        longitude: parseFloat(row.longitude)
      };

      if (cityData) {
        await supabase
          .from('cities')
          .update(cityRecord)
          .eq('id', cityData.id);
      } else {
        await supabase
          .from('cities')
          .insert(cityRecord);
      }
    }
  };

  const importServices = async (rows: any[]) => {
    for (const row of rows) {
      const { data: serviceData } = await supabase
        .from('services')
        .select('id')
        .eq('slug', row.slug)
        .single();

      const serviceRecord = {
        title: row.title,
        slug: row.slug,
        description: row.description,
        image_url: row.image_url,
        features: row.features.split(',')
      };

      if (serviceData) {
        await supabase
          .from('services')
          .update(serviceRecord)
          .eq('id', serviceData.id);
      } else {
        await supabase
          .from('services')
          .insert(serviceRecord);
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Import Data</h2>
      
      <div className="space-y-8">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>Download the template Excel file</li>
            <li>Fill in your data following the template format</li>
            <li>Upload the completed file to import the data</li>
          </ol>
        </div>

        <div className="flex justify-center">
          <button
            onClick={downloadTemplate}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Template
          </button>
        </div>

        <div className="border-t border-gray-200 pt-8">
          <div className="max-w-xl mx-auto">
            <label className="block text-center">
              <div className="flex flex-col items-center p-8 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mb-4" />
                <span className="text-sm font-medium text-gray-700">
                  {importing ? 'Importing...' : 'Choose Excel file to import'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  .xlsx or .xls files only
                </span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  disabled={importing}
                  className="hidden"
                />
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}